import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiSearch,
  FiHash,
  FiMessageSquare,
  FiAlertCircle,
  FiTrendingUp,
  FiClock,
  FiThumbsUp,
  FiX,
} from "react-icons/fi";
import { Pin } from "lucide-react";
import communityService from "../../../services/communityService";
import communityFeedService from "../../../services/communities/communityFeedService";
import { useSavedThreadIds, useUnsaveThread } from "../../../services/communities";
import CreateThreadModal from "./CreateThreadModal";
import ThreadCard from "./ThreadCard";
import SaveThreadModal from "../feed/SaveThreadModal";
import "./CommunityThreads.css";
import "./cth-main.css";

const CommunityThreads = ({
  communityId,
  community,
  isModeratorOrCreator,
  isReadOnly = false,
  userMembership = null,
}) => {
  const navigate = useNavigate();

// Check if user can create threads
  const canCreateThreads = !isReadOnly && userMembership?.role;

  // Saved threads hooks
  const { data: savedData } = useSavedThreadIds();
  const unsaveThreadMutation = useUnsaveThread();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedThreadForSave, setSelectedThreadForSave] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [sortBy, setSortBy] = useState("hot"); // Default to hot sorting per Guide 4
  const [statusFilter, setStatusFilter] = useState("all"); // Changed to "all" to distinguish from empty
  const [isSearching, setIsSearching] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNext: false,
    hasPrev: false,
  });

  // Popular tags from community
  const [popularTags, setPopularTags] = useState([
    "research",
    "discussion",
    "question",
    "announcement",
    "collaboration",
  ]);

  const fetchAllThreads = async (filters) => {
// Strategy 1: Try without status parameter
    try {
const response1 = await communityService.getCommunityThreads(
        communityId,
        filters
      );
      if (response1.success && response1.data.threads.length > 0) {
setThreads(response1.data.threads);
        setPagination(response1.data.pagination);
        return { success: true, data: response1.data };
      }
} catch (error) { /* ignored */ }

    // Strategy 2: Try with explicit status values and combine results
    try {
const statusTypes = ["active", "locked", "archived"];
      let allThreads = [];
      let totalItems = 0;

      for (const status of statusTypes) {
        try {
          const statusFilters = { ...filters, status };
          const response = await communityService.getCommunityThreads(
            communityId,
            statusFilters
          );
          if (response.success && response.data.threads) {
allThreads = [...allThreads, ...response.data.threads];
            totalItems += response.data.pagination?.totalItems || 0;
          }
        } catch (statusError) { /* ignored */ }
      }

      if (allThreads.length > 0) {
        // Sort threads by creation date (newest first)
        allThreads.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        // Move pinned threads to the top
        const pinnedThreads = allThreads.filter((t) => t.isPinned);
        const unpinnedThreads = allThreads.filter((t) => !t.isPinned);
        const sortedThreads = [...pinnedThreads, ...unpinnedThreads];
return {
          success: true,
          data: {
            threads: sortedThreads,
            pagination: {
              currentPage: 1,
              totalPages: Math.ceil(totalItems / filters.limit),
              totalItems: allThreads.length,
              itemsPerPage: filters.limit,
              hasNext: false,
              hasPrev: false,
            },
          },
        };
      }
    } catch (error) { /* ignored */ }

    // If all strategies fail, return empty result
return {
      success: true,
      data: {
        threads: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: filters.limit,
          hasNext: false,
          hasPrev: false,
        },
      },
    };
  };

  const tryFetchStrategies = async (communityId, filters) => {
    try {
      setLoading(true);

      // Strategy 1: Standard request (no status parameter for "all")
      const response1 = await communityService.getCommunityThreads(
        communityId,
        filters
      );

      if (response1.success && response1.data.threads.length > 0) {
// Debug: Check if threads have flair data
        const threadsWithFlair = response1.data.threads.filter((t) => t.flair);
if (threadsWithFlair.length > 0) {
}

        // Sort threads to show pinned threads first
        const allThreads = response1.data.threads;
        const pinnedThreads = allThreads.filter((t) => t.isPinned);
        const unpinnedThreads = allThreads.filter((t) => !t.isPinned);
        const sortedThreads = [...pinnedThreads, ...unpinnedThreads];
setThreads(sortedThreads);
        setPagination(response1.data.pagination);
        return {
          success: true,
          data: { ...response1.data, threads: sortedThreads },
        };
      }
setThreads([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasNext: false,
        hasPrev: false,
      });
      setError(null);
    } catch (error) {
setError(error.message || "Failed to fetch threads");
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchThreads = useCallback(async () => {
    try {
      setIsSearching(debouncedSearchTerm.length > 0);
      
      const filters = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        sort: sortBy, // ✅ Use 'sort' parameter per Guide 4
      };

      // Handle status filter - don't send status parameter for "all"
      if (statusFilter && statusFilter !== "all") {
        filters.status = statusFilter;
      }

      // Use debounced search term for API call
      if (debouncedSearchTerm.trim()) {
        filters.search = debouncedSearchTerm.trim();
      }

      if (selectedTag) {
        filters.tags = selectedTag;
      }

      await tryFetchStrategies(communityId, filters);
    } catch (error) {
setError(error.message || "Failed to fetch threads");
    }
  }, [
    communityId,
    pagination.currentPage,
    debouncedSearchTerm,
    selectedTag,
    sortBy,
    statusFilter,
  ]);

  // Debounce search term - 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearchTerm, selectedTag, sortBy, statusFilter]);

  const handleThreadCreated = (newThread) => {
// Add new thread and ensure proper sorting (pinned first)
    setThreads((prev) => {
      const allThreads = [newThread, ...prev];
      const pinnedThreads = allThreads.filter((t) => t.isPinned);
      const unpinnedThreads = allThreads.filter((t) => !t.isPinned);
      const sortedThreads = [...pinnedThreads, ...unpinnedThreads];
return sortedThreads;
    });

    setShowCreateModal(false);
    toast.success("Discussion created successfully!");
  };

  const handleThreadClick = (thread) => {
// Navigate to thread detail page - uses persistent navigation pattern
    // This will show ThreadDetailSection via the PersistentCommunitiesLayout
    navigate(`/communities/${communityId}/threads/${thread._id}`);
  };

  const handleThreadVote = async (threadId) => {
    try {
      const response = await communityService.voteThread(communityId, threadId);
      if (response.success) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread._id === threadId
              ? {
                  ...thread,
                  upvoteCount: response.data.upvoteCount,
                  downvoteCount:
                    response.data.downvoteCount || thread.downvoteCount,
                  score: response.data.score || thread.score,
                  userHasUpvoted: response.data.userHasUpvoted,
                  userHasDownvoted: response.data.userHasDownvoted || false,
                }
              : thread
          )
        );
      }
    } catch (error) {
toast.error("Failed to vote on discussion");
    }
  };

  const handleThreadDownvote = async (threadId) => {
    try {
      const response = await communityFeedService.downvoteThread(
        communityId,
        threadId
      );
      if (response.success) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread._id === threadId
              ? {
                  ...thread,
                  upvoteCount: response.data.upvoteCount || thread.upvoteCount,
                  downvoteCount: response.data.downvoteCount,
                  score: response.data.score || thread.score,
                  userHasUpvoted: response.data.userHasUpvoted || false,
                  userHasDownvoted: response.data.userHasDownvoted,
                }
              : thread
          )
        );
      }
    } catch (error) {
toast.error("Failed to downvote discussion");
    }
  };

  const handleThreadSave = async (thread) => {
    if (thread.isSaved) {
      // Thread is already saved - unsave it directly
      try {
        await unsaveThreadMutation.mutateAsync({ threadId: thread._id });
      } catch (error) { /* ignored */ }
    } else {
      // Thread is not saved - open modal for collection selection
      setSelectedThreadForSave(thread);
      setShowSaveModal(true);
    }
  };

  const handleThreadPin = async (threadId) => {
    if (!isModeratorOrCreator) return;

    try {
      const response = await communityService.pinThread(communityId, threadId);
      if (response.success) {
        // Update the thread with new pin status
        const updatedThreads = threads.map((thread) =>
          thread._id === threadId
            ? {
                ...thread,
                isPinned: response.data.isPinned,
                pinnedAt: response.data.pinnedAt,
              }
            : thread
        );

        // Re-sort threads to show pinned threads first
        const pinnedThreads = updatedThreads.filter((t) => t.isPinned);
        const unpinnedThreads = updatedThreads.filter((t) => !t.isPinned);
        const sortedThreads = [...pinnedThreads, ...unpinnedThreads];
setThreads(sortedThreads);
        toast.success(
          response.data.isPinned ? "Discussion pinned" : "Discussion unpinned"
        );
      }
    } catch (error) {
toast.error("Failed to pin discussion");
    }
  };

  const handleThreadLock = async (threadId) => {
    if (!isModeratorOrCreator) return;

    try {
      const response = await communityService.lockThread(communityId, threadId);
      if (response.success) {
        setThreads((prev) =>
          prev.map((thread) =>
            thread._id === threadId
              ? {
                  ...thread,
                  status: response.data.status,
                  lockedAt: response.data.lockedAt,
                }
              : thread
          )
        );
        toast.success(
          response.data.status === "locked"
            ? "Discussion locked"
            : "Discussion unlocked"
        );
      }
    } catch (error) {
toast.error("Failed to lock discussion");
    }
  };

  const handleThreadUpdated = (updatedThread) => {
    // Find the thread in current list
    const currentThread = threads.find((t) => t._id === updatedThread._id);

    if (currentThread) {
      const viewCountChanged =
        currentThread.viewCount !== updatedThread.viewCount;
      const viewCountDiff = updatedThread.viewCount - currentThread.viewCount;

      if (viewCountChanged) {
}

      // Update the specific thread immediately without full refresh and maintain pinned-first order
      setThreads((prev) => {
        const updated = prev.map((thread) =>
          thread._id === updatedThread._id
            ? { ...thread, ...updatedThread }
            : thread
        );

        // Re-sort to maintain pinned threads first
        const pinnedThreads = updated.filter((t) => t.isPinned);
        const unpinnedThreads = updated.filter((t) => !t.isPinned);
        return [...pinnedThreads, ...unpinnedThreads];
      });

      // Only refresh if there are significant changes beyond view count
      const hasSignificantChanges =
        currentThread.replyCount !== updatedThread.replyCount ||
        currentThread.upvoteCount !== updatedThread.upvoteCount ||
        currentThread.status !== updatedThread.status ||
        currentThread.isPinned !== updatedThread.isPinned;

      if (hasSignificantChanges) {
fetchThreads();
      } else if (viewCountChanged) {
}
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  // ✅ Guide 4 compliant sort options (using 'sort' parameter)
  const sortOptions = [
    { value: "hot", label: "🔥 Hot", description: "Trending discussions" },
    { value: "new", label: "🆕 New", description: "Recently created" },
    { value: "top", label: "👍 Top", description: "Most upvoted" },
    { value: "rising", label: "⬆️ Rising", description: "Rapidly growing" },
  ];

  if (loading && threads.length === 0) {
    return (
      <div className="community-threads">
        <div className="threads-loading">
          <div className="threads-loading-spinner"></div>
          <p>Loading discussions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-threads">
      {/* ===== REDESIGNED THREAD TOOLBAR (tbar-*) ===== */}
      <motion.div
        className="tbar-container"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {/* Main Controls Row */}
        <div className="tbar-controls">
          {/* Segmented Sort Control */}
          <div className="tbar-sort-group">
            {[
              { value: 'hot', icon: FiTrendingUp, label: 'Hot' },
              { value: 'new', icon: FiClock, label: 'New' },
              { value: 'top', icon: FiThumbsUp, label: 'Top' },
            ].map((option) => {
              const Icon = option.icon;
              const isActive = sortBy === option.value;
              return (
                <motion.button
                  key={option.value}
                  className={`tbar-sort-btn ${isActive ? 'active' : ''}`}
                  onClick={() => setSortBy(option.value)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Icon size={15} />
                  <span>{option.label}</span>
                  {isActive && (
                    <motion.div
                      className="tbar-sort-indicator"
                      layoutId="sortIndicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Search Field - Simplified */}
          <div className={`tbar-search ${searchTerm ? 'has-value' : ''}`}>
            <FiSearch size={16} className={`tbar-search-icon ${isSearching && loading ? 'tbar-spin' : ''}`} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="tbar-search-clear"
                onClick={() => setSearchTerm('')}
                type="button"
              >
                <FiX size={12} />
              </button>
            )}
          </div>

          {/* Create Button */}
          {canCreateThreads && (
            <motion.button
              className="tbar-create-btn"
              onClick={() => setShowCreateModal(true)}
              whileHover={{ scale: 1.05, boxShadow: '0 4px 20px rgba(59, 130, 246, 0.35)' }}
              whileTap={{ scale: 0.95 }}
            >
              <FiPlus size={18} />
              <span>New</span>
            </motion.button>
          )}
        </div>

        {/* Tag Filter Row */}
        <div className="tbar-tags-row">
          <motion.button
            className={`tbar-tag ${selectedTag === '' ? 'active' : ''}`}
            onClick={() => setSelectedTag('')}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.96 }}
          >
            All
          </motion.button>
          {popularTags.map((tag) => (
            <motion.button
              key={tag}
              className={`tbar-tag ${selectedTag === tag ? 'active' : ''}`}
              onClick={() => setSelectedTag(tag)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.96 }}
            >
              <FiHash size={11} />
              {tag}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Threads List */}
      <div className="threads-list">
        {/* Error State */}
        {error && (
          <div className="threads-error">
            <div className="error-content">
              <FiAlertCircle size={48} className="error-icon" />
              <h3>Unable to load discussions</h3>
              <p>{error}</p>
              {error.includes("join this community") && (
                <div className="error-actions">
                  <button
                    className="btn-primary"
                    onClick={() => {
                      // Navigate back to community page to join
                      navigate(`/communities/${communityId}`);
                    }}
                  >
                    View Community
                  </button>
                </div>
              )}
              {error.includes("log in") && (
                <div className="error-actions">
                  <button
                    className="btn-primary"
                    onClick={() => navigate("/signin")}
                  >
                    Sign In
                  </button>
                </div>
              )}
              <button
                className="btn-secondary"
                onClick={() => {
                  setError(null);
                  fetchThreads();
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {threads.length === 0 && !loading ? (
          <div className="threads-empty">
            <FiMessageSquare size={48} />
            <h3>No discussions yet</h3>
            <p>
              {canCreateThreads
                ? "Be the first to start a discussion in this community!"
                : isReadOnly
                ? "This community doesn't have any discussions yet."
                : "Join this community to start discussions!"}
            </p>
            {canCreateThreads && (
              <button
                className="create-thread-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <FiPlus size={16} />
                Start Discussion
              </button>
            )}
          </div>
        ) : (
          <>
            {(() => {
              // Enhance threads with saved status from local cache
              const enhancedThreads = threads.map((thread) => ({
                ...thread,
                isSaved: savedData?.savedSet?.has(thread._id) ?? thread.userHasSaved ?? false,
              }));
              
              // Separate pinned and regular threads
              const pinnedThreads = enhancedThreads.filter((t) => t.isPinned);
              const regularThreads = enhancedThreads.filter((t) => !t.isPinned);

              return (
                <>
                  {/* Pinned Threads Section */}
                  {pinnedThreads.length > 0 ? (
                    <div className="pinned-threads-section">
                      <div className="pinned-threads-header">
                        <h3>
                          <Pin size={16} className="pin-icon" />
                          Pinned Discussions
                        </h3>
                        <span className="pinned-threads-count">
                          {pinnedThreads.length}
                        </span>
                      </div>
                      <div className="threads-list">
                        {pinnedThreads.map((thread) => (
                          <ThreadCard
                            key={thread._id}
                            thread={thread}
                            communityId={communityId}
                            onThreadClick={handleThreadClick}
                            onVote={handleThreadVote}
                            onDownvote={handleThreadDownvote}
                            onSave={handleThreadSave}
                            onPin={handleThreadPin}
                            onLock={handleThreadLock}
                            onThreadUpdated={handleThreadUpdated}
                            isModeratorOrCreator={isModeratorOrCreator}
                            isReadOnly={isReadOnly}
                            canInteract={!isReadOnly && userMembership?.role}
                            showCommunityInfo={false}
                            formatTimeAgo={formatTimeAgo}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    // Show tutorial hint for moderators when no pinned threads exist
                    isModeratorOrCreator &&
                    regularThreads.length > 0 && (
                      <div className="pin-tutorial-hint">
                        <Pin size={16} className="pin-icon" />
                        <div className="pin-tutorial-text">
                          <strong>Pro Tip:</strong> Pin important discussions to
                          keep them at the top! Use the pin option in the thread
                          menu to highlight key conversations.
                        </div>
                      </div>
                    )
                  )}

                  {/* Regular Threads Section */}
                  {regularThreads.length > 0 && (
                    <div className="regular-threads-section">
                      {pinnedThreads.length > 0 && (
                        <div className="regular-threads-header">
                          <FiMessageSquare size={14} />
                          Recent Discussions ({regularThreads.length})
                        </div>
                      )}
                      <div className="threads-list">
                        {regularThreads.map((thread) => (
                          <ThreadCard
                            key={thread._id}
                            thread={thread}
                            communityId={communityId}
                            onThreadClick={handleThreadClick}
                            onVote={handleThreadVote}
                            onDownvote={handleThreadDownvote}
                            onSave={handleThreadSave}
                            onPin={handleThreadPin}
                            onLock={handleThreadLock}
                            onThreadUpdated={handleThreadUpdated}
                            isModeratorOrCreator={isModeratorOrCreator}
                            isReadOnly={isReadOnly}
                            canInteract={!isReadOnly && userMembership?.role}
                            showCommunityInfo={false}
                            formatTimeAgo={formatTimeAgo}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="threads-pagination">
                <button
                  className="pagination-btn"
                  disabled={!pagination.hasPrev}
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage - 1,
                    }))
                  }
                >
                  Previous
                </button>

                <span className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  className="pagination-btn"
                  disabled={!pagination.hasNext}
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }))
                  }
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Loading indicator for pagination */}
      {loading && threads.length > 0 && (
        <div className="threads-loading-more">
          <div className="loading-spinner-small"></div>
          <span>Loading more discussions...</span>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateThreadModal
          communityId={communityId}
          community={community}
          onClose={() => setShowCreateModal(false)}
          onThreadCreated={handleThreadCreated}
        />
      )}

      {/* Save Thread Modal */}
      {showSaveModal && selectedThreadForSave && (
        <SaveThreadModal
          thread={selectedThreadForSave}
          onClose={() => {
            setShowSaveModal(false);
            setSelectedThreadForSave(null);
          }}
        />
      )}
    </div>
  );
};

export default CommunityThreads;
